import { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { toPersian } from '../../utils/persianNumber';
import {
  JALALI_WEEKDAYS, buildJalaliMonthGrid, jalaliLabel,
  addDays, startOfDay, isSameDay, toJalali,
} from '../../utils/jalali';

const BLUE = '#29AAE1';
const BLUE_SOFT = '#EDF3FC';
const BLUE_MID = '#B7C9EC';
const POPUP_WIDTH = 300;
const POPUP_Z = 10000;

const fmt = (d) => {
  if (!d) return '';
  const { jy, jm, jd } = toJalali(d);
  return toPersian(`${jy}/${String(jm).padStart(2, '0')}/${String(jd).padStart(2, '0')}`);
};

const PRESETS = [
  { label: 'امروز', date: () => startOfDay(new Date()) },
  { label: 'دیروز', date: () => addDays(startOfDay(new Date()), -1) },
];

function usePopupPosition(open, triggerRef, popupRef) {
  const [style, setStyle] = useState(null);

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger || !open) return;

    const rect = trigger.getBoundingClientRect();
    const popupH = popupRef.current?.offsetHeight || 380;
    const gap = 8;
    let top = rect.bottom + gap;

    if (top + popupH > window.innerHeight - gap) {
      const above = rect.top - gap - popupH;
      if (above >= gap) top = above;
    }

    let left = rect.right - POPUP_WIDTH;
    left = Math.max(gap, Math.min(left, window.innerWidth - POPUP_WIDTH - gap));

    setStyle({
      position: 'fixed',
      top,
      left,
      width: POPUP_WIDTH,
      zIndex: POPUP_Z,
    });
  }, [open, triggerRef, popupRef]);

  useLayoutEffect(() => {
    if (!open) {
      setStyle(null);
      return undefined;
    }
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open, updatePosition]);

  useLayoutEffect(() => {
    if (open) updatePosition();
  }, [open, updatePosition]);

  return style;
}

// Single-day Jalali calendar — popup portals to body so overflow:hidden parents cannot clip it.
export default function JalaliDatePicker({ value, onChange, placeholder = 'انتخاب تاریخ' }) {
  const [open, setOpen] = useState(false);
  const [viewAnchor, setViewAnchor] = useState(value || new Date());
  const triggerRef = useRef(null);
  const popupRef = useRef(null);
  const popupStyle = usePopupPosition(open, triggerRef, popupRef);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (triggerRef.current?.contains(e.target)) return;
      if (popupRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  useEffect(() => {
    if (open) setViewAnchor(value || new Date());
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const grid = buildJalaliMonthGrid(viewAnchor);

  const goMonth = (delta) => {
    setViewAnchor(delta > 0 ? addDays(grid.lastDate, 1) : addDays(grid.firstDate, -1));
  };

  const pickDay = (date) => {
    onChange?.(startOfDay(date));
    setOpen(false);
  };

  const applyPreset = (preset) => {
    const d = preset.date();
    onChange?.(d);
    setViewAnchor(d);
    setOpen(false);
  };

  const popup = open && popupStyle && createPortal(
    <div
      ref={popupRef}
      className="fade-in"
      style={{
        ...popupStyle,
        background: '#fff',
        borderRadius: 16,
        border: `1px solid ${BLUE_MID}`,
        boxShadow: '0 20px 48px rgba(35,58,122,0.18)',
        padding: 18,
      }}
    >
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
        {PRESETS.map((p) => (
          <button key={p.label} type="button" onClick={() => applyPreset(p)}
            style={{
              padding: '5px 10px', borderRadius: 20, border: `1px solid ${BLUE_MID}`,
              background: BLUE_SOFT, color: BLUE, fontSize: 11, fontWeight: 600,
              cursor: 'pointer', fontFamily: 'inherit',
            }}
          >{p.label}</button>
        ))}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <button type="button" onClick={() => goMonth(-1)} aria-label="ماه قبل" style={navBtnStyle}>‹</button>
        <span style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>
          {jalaliLabel(grid.jy, grid.jm)}
        </span>
        <button type="button" onClick={() => goMonth(1)} aria-label="ماه بعد" style={navBtnStyle}>›</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', marginBottom: 4 }}>
        {JALALI_WEEKDAYS.map((w) => (
          <div key={w} style={{ textAlign: 'center', fontSize: 10.5, fontWeight: 700, color: '#94a3b8', padding: '4px 0' }}>{w}</div>
        ))}
      </div>

      <div className="fade-in" style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {grid.weeks.map((week, wi) => (
          <div key={wi} style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)' }}>
            {week.map(({ date, jd, inMonth }) => {
              const selected = isSameDay(date, value);
              const isToday = isSameDay(date, startOfDay(new Date()));
              return (
                <button
                  key={date.toISOString()}
                  type="button"
                  onClick={() => pickDay(date)}
                  disabled={!inMonth}
                  style={{
                    position: 'relative', height: 32, border: 'none', background: 'none',
                    cursor: inMonth ? 'pointer' : 'default', fontFamily: 'inherit',
                    fontSize: 12.5, color: !inMonth ? '#cbd5e1' : selected ? '#fff' : '#334155',
                    fontWeight: selected ? 700 : isToday ? 700 : 500,
                    zIndex: 1,
                  }}
                >
                  {inMonth && selected && (
                    <span style={{
                      position: 'absolute', inset: '2px 4px', borderRadius: 999, background: BLUE,
                      zIndex: -1, boxShadow: '0 4px 10px rgba(35,58,122,0.4)',
                    }} />
                  )}
                  {inMonth && isToday && !selected && (
                    <span style={{ position: 'absolute', bottom: 3, left: '50%', transform: 'translateX(-50%)', width: 4, height: 4, borderRadius: '50%', background: BLUE }} />
                  )}
                  {toPersian(jd)}
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </div>,
    document.body,
  );

  return (
    <div ref={triggerRef} style={{ position: 'relative', fontFamily: 'inherit', display: 'inline-block' }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="dialog"
        style={{
          display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px',
          border: `1.5px solid ${open ? BLUE : '#e2e8f0'}`, borderRadius: 10,
          background: open ? BLUE_SOFT : '#fff', cursor: 'pointer', fontFamily: 'inherit',
          fontSize: 13, color: '#334155', transition: 'all 0.18s ease', minWidth: 180,
        }}
      >
        <span style={{ fontSize: 15 }}>📅</span>
        <span style={{ fontWeight: 600 }}>{value ? fmt(value) : placeholder}</span>
      </button>
      {popup}
    </div>
  );
}

const navBtnStyle = {
  width: 28, height: 28, borderRadius: 8, border: `1px solid ${BLUE_MID}`,
  background: BLUE_SOFT, color: BLUE, fontSize: 16, fontWeight: 700,
  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
  fontFamily: 'inherit',
};
